import React, { useEffect, useRef } from 'react';
import config from 'config';
import { IS_SNAP, ADSENSE_ACTIVE } from 'variables/main';

const shouldLoadAds = () => {
  // Master kill-switch — GOOGLE_ADSENSE_ACTIVE GitHub var, baked at build.
  if (!ADSENSE_ACTIVE) return false;

  // Don't load ads in snapshots
  if (IS_SNAP) return false;

  // Don't load ads in development environment
  const { application: { environment }, caPub } = config;
  if (environment === 'dev') return false;

  // Brands without a configured AdSense publisher ID (e.g. jobalto.com,
  // caPub is "") must not render the <ins> placeholder. AdSense's hardcoded
  // fallback client wouldn't be authorised for the brand's domain, so the
  // slot never fills, and the reserved 280 px sits below the footer as
  // dead space. Skipping renders nothing instead.
  if (!caPub) return false;

  // Don't load ads on localhost
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0') {
    return false;
  }

  return true;
};

const AdSense = ({
  adSlot,
  // Default to the active brand's publisher ID from config.json. The previous
  // hardcoded `ca-pub-…` defaulted every brand to profitroll's account, which
  // is why jobalto pages were calling AdSense with the wrong publisher.
  adClient = config.caPub,
  adFormat = 'fluid',
  adLayout,
  adLayoutKey,
  style = { display: 'block' },
  className = 'adsbygoogle'
}) => {
  const adRef = useRef(null);
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    if (!shouldLoadAds()) {
      return;
    }

    // Initialize adsbygoogle array if it doesn't exist
    window.adsbygoogle = window.adsbygoogle || [];

    // Load the AdSense script if not already loaded
    const existingScript = document.querySelector(
      `script[src*="pagead/js/adsbygoogle.js"]`
    );

    if (!existingScript && !scriptLoadedRef.current) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adClient}`;
      script.crossOrigin = 'anonymous';
      script.onerror = () => {
        // Silently fail to avoid console noise
      };
      document.head.appendChild(script);
      scriptLoadedRef.current = true;
    }

    let pushed = false;
    let resizeObserver = null;
    let pollId = null;

    const pushAd = () => {
      if (pushed || !adRef.current) return;
      // Google's script throws "TagError: No slot size for availableWidth=0"
      // when the container is collapsed (lazy section, hidden parent). Skip
      // the push until the slot actually has width.
      if (adRef.current.offsetWidth <= 0) return;
      pushed = true;
      try {
        window.adsbygoogle.push({});
      } catch (error) {
        // Silently fail to avoid console noise
      }
    };

    const startWatching = () => {
      if (!adRef.current) return;
      pushAd();
      if (pushed) return;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => pushAd());
        resizeObserver.observe(adRef.current);
      } else {
        // Fallback for environments without ResizeObserver: poll briefly.
        pollId = setInterval(() => {
          pushAd();
          if (pushed) {
            clearInterval(pollId);
            pollId = null;
          }
        }, 250);
        setTimeout(() => {
          if (pollId) {
            clearInterval(pollId);
            pollId = null;
          }
        }, 5000);
      }
    };

    let scriptCheckId = null;
    let scriptTimeoutId = null;

    if (existingScript || scriptLoadedRef.current) {
      setTimeout(startWatching, 100);
    } else {
      scriptCheckId = setInterval(() => {
        const script = document.querySelector(
          `script[src*="pagead/js/adsbygoogle.js"]`
        );
        if (script) {
          clearInterval(scriptCheckId);
          scriptCheckId = null;
          setTimeout(startWatching, 100);
        }
      }, 100);
      scriptTimeoutId = setTimeout(() => {
        if (scriptCheckId) {
          clearInterval(scriptCheckId);
          scriptCheckId = null;
        }
        startWatching();
      }, 5000);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (pollId) clearInterval(pollId);
      if (scriptCheckId) clearInterval(scriptCheckId);
      if (scriptTimeoutId) clearTimeout(scriptTimeoutId);
    };
  }, [adClient]);

  if (!shouldLoadAds()) {
    return null;
  }

  // Reserve vertical space before the ad fills to keep CLS low. AdSense
  // initially renders <ins> at 0×0 and only sizes it once the auction and
  // creative finish loading; without a min-height the page jumps by the
  // full ad height on insertion. 280 px is the typical in-article/fluid
  // size — when the actual fill is taller, the shift is bounded to the
  // delta instead of the full creative. Caller-provided minHeight wins.
  const adProps = {
    className,
    style: { minHeight: 280, ...style },
    'data-ad-client': adClient,
    'data-ad-slot': adSlot,
    'data-ad-format': adFormat,
    ref: adRef
  };

  if (adLayout) {
    adProps['data-ad-layout'] = adLayout;
  }

  if (adLayoutKey) {
    adProps['data-ad-layout-key'] = adLayoutKey;
  }

  return <ins {...adProps} />;
};

export default AdSense;

